import React, {
  useCallback,
  useRef,
  createRef,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Grid from 'apollo-react/components/Grid';
import CircularProgress from 'apollo-react/components/CircularProgress';
import { toast } from 'react-toastify';
import MultiFormView from '../../../common/MultiFormView/MultiFormView';
import PaymentConfirmationModal from '../../../Payment/PaymentConfirmationModal';
import useMultiFormSubmission from '../../../../hooks/features/customer/useMultiFormSubmission';
import useSubformNavigation from '../../../../hooks/ui/navigation/useSubformNavigation';
import useUnsavedChangesWarning from '../../../../hooks/ui/navigation/useUnsavedChangesWarning';
import useSubformApprovalsApi from '../../../../hooks/api/subformApprovals/useSubformApprovalsApi';
import usePendingFieldEdits from '../../../../hooks/ui/fieldActions/usePendingFieldEdits';
import { formatValidationError } from '../../../../utils/formCrafter/formatValidationError';
import useDraftsApi from '../../../../hooks/api/drafts/useDraftsApi';
import { useFilesApi } from '../../../../hooks/api/files';
import {
  createStagedFileKey,
  normalizeFileId,
} from '../../../../utils/stagedFiles';
import { APPROVAL_TYPE_CODES } from '../../../../constants/workflow.constant';
import {
  MultiFormProvider,
  useMultiForm,
} from '../../../../contexts/MultiFormContext';
import HcpLicensesList from '../New_Requests/HcpLicensesList/HcpLicensesList';
import HcpLicensesListView from '../New_Requests/HcpLicensesList/HcpLicensesListView';
import HcpProfileFormSectionsPanel from '../New_Requests/HcpProfileFormSectionsPanel';
import {
  computeEffectiveHasHcpLicensesTab,
  normalizeWorkflowLicenseMode,
} from '../../../../utils/workflowLicenseMode';
import {
  isLicensingToolMode,
  useLicensingExperienceOptional,
} from '../../../../contexts/LicensingExperienceContext';
import styles from './MultiFormSubmission.module.css';

function formHasAttachProfileSnapshot(form) {
  const flag =
    form?.attach_end_user_profile_snapshot ??
    form?.form?.attach_end_user_profile_snapshot;
  return flag === true || flag === 1;
}

/** FormSections path: submit / draft via MultiFormContext. */
function FormSectionsSubmissionActions({ disabled }) {
  const { t } = useTranslation();
  const { submitAllForms, saveAsDraft, submissionState } = useMultiForm();
  const [savingDraft, setSavingDraft] = useState(false);
  const submitting = Boolean(submissionState?.isSubmitting);

  const handleSubmit = useCallback(async () => {
    await submitAllForms();
  }, [submitAllForms]);

  const handleDraft = useCallback(async () => {
    setSavingDraft(true);
    try {
      const result = await saveAsDraft();
      if (result?.success) {
        toast.success(t('multiFormSubmission.draftSaved') || 'Draft saved', {
          position: 'top-center',
        });
      } else {
        toast.error(
          result?.error || t('multiFormSubmission.failedToSaveDraft'),
          { position: 'top-center' }
        );
      }
    } catch (error) {
      console.error(error);
      toast.error(t('multiFormSubmission.failedToSaveDraft'), {
        position: 'top-center',
      });
    } finally {
      setSavingDraft(false);
    }
  }, [saveAsDraft, t]);

  if (disabled) return null;

  return (
    <div className={styles.submitContainer}>
      <button
        type="button"
        className={styles.draftButton}
        onClick={handleDraft}
        disabled={savingDraft || submitting}
      >
        {savingDraft
          ? t('multiFormSubmission.savingDraft')
          : t('multiFormSubmission.saveAsDraft')}
      </button>
      <button
        type="button"
        className={styles.submitButton}
        onClick={handleSubmit}
        disabled={submitting || savingDraft}
      >
        {submitting
          ? t('multiFormSubmission.submitting')
          : t('multiFormSubmission.submitAllForms')}
      </button>
    </div>
  );
}

/**
 * MultiFormSubmission - Component for wizard-style multi-form submission
 *
 * Displays multiple forms as horizontal tabs with vertical subform navigation.
 * Used for new request submission and send-back editing.
 *
 * @param {Object} props
 * @param {number} props.workflowVersionId - Workflow version ID (for new requests)
 * @param {number} props.requestId - Request ID (for send-back/edit)
 * @param {number} props.requestProgressId - Request progress ID (for advancing existing requests)
 * @param {number} props.stageId - Current stage ID (for progress view - shows forms for this stage)
 * @param {string} props.customRequestName - Optional custom request name
 * @param {Object} props.existingFilledForms - Pre-filled form data (for send-back)
 * @param {Array} props.targetStageIds - Specific stages to show (for send-back)
 * @param {boolean} props.disabled - Whether form is read-only
 * @param {Object} props.comments - Comments object keyed by form ID
 * @param {Function} props.onCommentsUpdate - Callback when comments change
 * @param {Object} props.commentsStatus - Pre-computed comment status by subformId { [subformId]: true } (for customer view)
 * @param {Array} props.allComments - All comments as flat array (for customer view - will be distributed to all forms)
 * @param {React.ReactNode} props.requestAuditTrailsSlot - Optional slot for Request Audit Trails tab
 * @param {React.ReactNode} props.additionalDocumentsSlot - Optional slot for Additional Documents tab
 * @param {React.ReactNode} props.requestClarificationWavesSlot - Optional slot for Request Clarification Waves tab
 * @param {boolean} props.showValidationIndicators - Show validation indicators in subform nav (default: true)
 * @param {React.ReactNode} props.topSlot - Optional slot for content at top of form (e.g., badges)
 * @param {boolean} props.enableAddComment - Explicit control for comment creation (overrides internal logic when provided)
 * @param {React.ReactNode} props.linkedStudiesSelectorSlot - Optional slot for Linked Studies selector tab (for new requests)
 * @param {React.ReactNode} props.linkedStudiesViewSlot - Optional slot for Linked Studies view tab (for existing requests)
 * @param {React.ReactNode} props.linkedSubmissionsViewSlot - Optional slot for Linked Submissions view tab
 * @param {React.ReactNode} props.parentRequestSelectorSlot - Optional slot for Parent Request selector tab
 * @param {Array} props.linkedRequestIds - Array of linked request IDs (for new requests with linked studies)
 * @param {number[]} props.parentRequestIds - Selected approved parent request IDs for sub-workflow create
 * @param {Function} props.onLinkedStudiesValidationError - Callback when linked studies validation fails
 * @param {boolean} props.isRequestActive - Whether the request is active/assigned
 * @param {boolean} props.showAllStages - Show every submission stage's form as a tab; forms outside the current stage are read-only
 */
const MultiFormSubmission = ({
  workflowVersionId,
  requestId,
  requestProgressId,
  stageId,
  customRequestName,
  existingFilledForms = {},
  targetStageIds = [],
  disabled = false,
  useListView = false,
  comments = {},
  onCommentsUpdate,
  commentsStatus = null,
  allComments = [],
  requestAuditTrailsSlot,
  additionalDocumentsSlot,
  additionalDocumentsComments,
  requestClarificationWavesSlot,
  isEditable = false,
  showValidationIndicators = true,
  topSlot,
  enableAddComment: enableAddCommentProp,
  linkedStudiesSelectorSlot,
  linkedStudiesViewSlot,
  linkedSubmissionsViewSlot,
  parentRequestSelectorSlot,
  linkedRequestIds = [],
  parentRequestIds = [],
  onLinkedStudiesValidationError,
  // Comment edit/delete props
  requestProgress,
  requestDateEnded,
  currentUserEmail,
  onCommentUpdated,
  onCommentDeleted,
  onSubformCommentAdded,
  // Props for draft functionality
  draftId = null, // If editing an existing draft
  enableDrafts = true, // Enable Save as Draft button
  enableEagerUpload = true, // Enable eager file upload
  isRequestActive = false,
  onDraftDataLoaded, // Callback when draft data is loaded: ({ additionalDocumentsComments, linkedRequestIds }) => void
  isSendbackMode = false,
  showAllStages = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  /**
   * ==========================================================================
   * STATE MANAGEMENT - Draft & Staged Files
   * ==========================================================================
   *
   * This component manages complex state for the Save As Draft feature.
   * Files are now managed through the centralized file module.
   *
   * FILE UPLOAD FLOW:
   * 1. User selects file -> FileFieldInput calls onStagedFileUpload
   * 2. File uploads to centralized files table with STAGED status
   * 3. File ID tracked in stagedFileIds and uploadedStagedFilesByField
   * 4. On "Save Draft": files remain in STAGED status, draft references them
   * 5. On "Submit": files promoted to AVAILABLE status, attached to form
   *
   * DRAFT LOADING FLOW:
   * 1. draftId passed as prop -> useMultiFormSubmission fetches draft
   * 2. draftStagedFiles populated with files from draft
   * 3. initializedFromDraft flag prevents re-initialization
   * 4. Files displayed in FileFieldInput via initialStagedFiles prop
   */

  /** @type {React.MutableRefObject<Object>} Refs to FormCrafterFormRenderer instances by form ID */
  const formRefsMap = useRef({});

  /** @type {Object} Validation errors by form ID and subform ID */
  const [validationErrors, setValidationErrors] = useState({});

  /** @type {Object} Subform approval statuses fetched from backend */
  const [subformApprovals, setSubformApprovals] = useState({});

  /** @type {Array} Comments added during current session (merged with allComments prop) */
  const [localComments, setLocalComments] = useState([]);

  /** @type {boolean} True while draft is being saved to prevent double-submission */
  const [savingDraft, setSavingDraft] = useState(false);

  const [showPaymentModal, setShowPaymentModal] = useState(false);

  /**
   * @type {Array<string>} All staged file IDs to include when saving draft or submitting.
   * Updated when:
   * - Draft is loaded (populated from draft.stagedFiles)
   * - User uploads new file (ID added)
   * - User deletes file (ID removed)
   */
  const [stagedFileIds, setStagedFileIds] = useState([]);

  /**
   * @type {boolean} Prevents re-initialization of stagedFileIds when draft data changes.
   * Set to true after initial population from draftStagedFiles.
   */
  const [initializedFromDraft, setInitializedFromDraft] = useState(false);

  /**
   * @type {Set<string>} IDs of draft files that user has deleted during this session.
   * Used to filter out deleted files from allStagedFiles even if they still
   * exist in draftStagedFiles (which comes from the hook and doesn't update).
   */
  const [removedDraftFileIds, setRemovedDraftFileIds] = useState(new Set());

  /**
   * @type {Array<number|string>} Deleted server file IDs confirmed during this session
   * when editing an existing draft. Used to keep draft deletions persistent.
   */
  // NOTE: sessionDraftDeletedFileIds removed - file deletions are now immediate
  // via dedicated endpoints (updateDraftDeletedFiles / deleteAttachedFiles).

  /**
   * @type {Object} Tracks newly uploaded staged files by field key.
   * Key format: "formId-subformId-fieldId" or "subformId-fieldId" for backward compatibility
   * Value: Array of file objects { id, name, size, type, stagedId, isStagedFile }
   *
   * Used for:
   * - Immediate validation (via ref for sync access)
   * - Combining with draftStagedFiles for allStagedFiles
   * - Passing to child components for file display
   */
  const [uploadedStagedFilesByField, setUploadedStagedFilesByField] = useState(
    {}
  );

  /**
   * @type {React.MutableRefObject<Object>} Synchronous mirror of uploadedStagedFilesByField.
   * React state updates are async; this ref provides immediate access for validation
   * that runs right after file upload before state batching completes.
   */
  const uploadedStagedFilesByFieldRef = useRef({});

  /**
   * @type {number} Trigger to force allStagedFiles memo recalculation.
   * Counter incremented after every mutation to uploadedStagedFilesByFieldRef.
   * Because refs don't trigger re-renders, this state value is listed as a
   * dependency in the combinedStagedFiles useMemo so it recalculates when
   * files are uploaded or deleted, even before async state updates complete.
   */
  const [stagedFilesTrigger, setStagedFilesTrigger] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);

  const markInteracted = useCallback(() => {
    if (!hasInteracted) setHasInteracted(true);
  }, [hasInteracted]);

  const {
    hasPending: hasPendingFieldEdits,
    markPending: markPendingEdit,
    markResolved: markResolvedEdit,
  } = usePendingFieldEdits();

  // Treat a non-empty custom request name as an interaction so the guard fires
  useEffect(() => {
    if (customRequestName?.trim()) markInteracted();
  }, [customRequestName, markInteracted]);

  const { allowNavigation, navigateBack } = useUnsavedChangesWarning(
    !disabled && hasInteracted
  );

  const { getApprovalsByRequest } = useSubformApprovalsApi();
  const {
    createOrUpdateDraft,
    updateDraftDeletedFiles,
    loading: draftLoading,
  } = useDraftsApi();
  // Unified file API - handles both staged (not yet attached) and server (attached) files
  const { uploadFile, deleteFile, deleteAttachedFiles } = useFilesApi();

  /**
   * Create a field key from upload options using centralized utility.
   * This ensures consistent key format across the application.
   */
  const getFieldKeyFromOptions = useCallback((options) => {
    try {
      return createStagedFileKey({
        formId: options?.formId,
        subformId: options?.subformId,
        fieldId: options?.fieldId,
      });
    } catch (error) {
      console.error('Error creating field key:', error);
      return null;
    }
  }, []);

  // Initialize local comments from allComments prop
  useEffect(() => {
    if (allComments.length > 0) {
      setLocalComments(allComments);
    }
  }, [allComments]);

  // Handler for when a new comment is added
  const handleCommentsUpdate = useCallback(
    (formId, updater) => {
      try {
        setLocalComments((prev) => {
          // updater is a function that takes previous comments and returns new array
          if (typeof updater === 'function') {
            return updater(prev);
          }
          return prev;
        });
        // Also call parent callback if provided
        if (onCommentsUpdate) {
          onCommentsUpdate(formId, updater);
        }
      } catch (error) {
        console.error('Error updating comments:', error);
      }
    },
    [onCommentsUpdate]
  );

  const {
    forms,
    activeFormIndex,
    activeSubforms,
    formData,
    loading,
    submitting,
    error,
    handleFormTabChange,
    handleSaveAllForms,
    draftStagedFiles,
    draftAdditionalDocumentsComments,
    draftLinkedRequestIds,
    draftParentRequestIds,
    workflowPaymentConfig,
    workflowLicenseMeta,
  } = useMultiFormSubmission({
    workflowVersionId,
    requestId,
    requestProgressId,
    stageId,
    customRequestName,
    existingFilledForms,
    targetStageIds,
    draftId,
    isRequestActive,
    showAllStages,
  });

  const { mode: licensingExperienceMode } = useLicensingExperienceOptional();

  const showHcpProfileFormSections = useMemo(
    () =>
      isLicensingToolMode(licensingExperienceMode) &&
      forms.some(formHasAttachProfileSnapshot),
    [licensingExperienceMode, forms]
  );

  const showHcpLicensesListTab = useMemo(() => {
    const mode = normalizeWorkflowLicenseMode(
      workflowLicenseMeta.workflow_license_mode,
      workflowLicenseMeta.license_renewal
    );
    return computeEffectiveHasHcpLicensesTab({
      workflowLicenseMode: mode,
      licenseRenewalFallback: workflowLicenseMeta.license_renewal,
      anySubmissionFormHasHcpLicenses:
        workflowLicenseMeta.anySubmissionFormHasHcpLicenses,
      entryFormHasHcpFlag: forms.some((f) => {
        const flag = f?.has_hcp_licenses_list ?? f?.form?.has_hcp_licenses_list;
        return flag === true || flag === 1;
      }),
      hcpListRowPresent: false,
    });
  }, [workflowLicenseMeta, forms]);

  const hcpLicensesListSlot = useMemo(() => {
    if (!showHcpLicensesListTab) return null;
    return (
      <MultiFormProvider
        workflowVersionId={workflowVersionId}
        requestId={requestId}
        requestProgressId={requestProgressId}
        studyName={customRequestName}
        isSendbackMode={isSendbackMode || !!requestId}
        draftId={draftId}
        licensingExperienceMode={licensingExperienceMode}
      >
        {disabled ? (
          <HcpLicensesListView />
        ) : (
          <HcpLicensesList isSendbackMode={isSendbackMode || !!requestId} />
        )}
      </MultiFormProvider>
    );
  }, [
    showHcpLicensesListTab,
    workflowVersionId,
    requestId,
    requestProgressId,
    customRequestName,
    isSendbackMode,
    draftId,
    licensingExperienceMode,
    disabled,
  ]);

  const activeStage = useMemo(
    () => forms[activeFormIndex]?.stage || null,
    [forms, activeFormIndex]
  );
  const paymentAmount = activeStage?.payment_amount;
  const paymentAmountValue =
    paymentAmount !== undefined ? Number(paymentAmount) : null;
  const hasValidAmount =
    paymentAmount !== undefined &&
    paymentAmount !== null &&
    paymentAmount !== '' &&
    !Number.isNaN(paymentAmountValue) &&
    paymentAmountValue >= 0;
  const paymentGatewayEnabled = Boolean(workflowPaymentConfig?.paymentEnabled);
  const wfPaymentProviderId = workflowPaymentConfig?.paymentProviderId;
  const wfPaymentCurrencyCode = workflowPaymentConfig?.paymentCurrency;
  const paymentConfigured = Boolean(
    paymentGatewayEnabled && wfPaymentProviderId && wfPaymentCurrencyCode
  );
  const currentApprovalCode =
    requestProgress?.StageRelation?.Stage?.ApprovalType?.code;
  const isSendBack = Boolean(
    currentApprovalCode === APPROVAL_TYPE_CODES.SEND_BACK
  );
  const paymentGatesActive =
    activeStage?.payment_enabled && hasValidAmount && paymentAmountValue > 0;
  const paymentRequirementsMet = paymentConfigured && paymentGatesActive;
  // Payment is required for all submission stages except send-back
  const requiresPayment = Boolean(!isSendBack && paymentRequirementsMet);
  const paymentCurrency = workflowPaymentConfig?.paymentCurrency || null;
  const canSubmitWithPayment = isSendBack
    ? true
    : !activeStage?.payment_enabled ||
      (paymentConfigured && hasValidAmount) ||
      (activeStage?.payment_enabled && !paymentGatewayEnabled);

  // Initialize staged file IDs from draft when loaded
  useEffect(() => {
    if (
      draftStagedFiles &&
      Object.keys(draftStagedFiles).length > 0 &&
      !initializedFromDraft
    ) {
      const allDraftFileIds = [];
      Object.values(draftStagedFiles).forEach((files) => {
        files.forEach((file) => {
          if (file.id || file.stagedId) {
            const normalized = normalizeFileId(file.id || file.stagedId);
            if (normalized) {
              allDraftFileIds.push(normalized);
            }
          }
        });
      });
      if (allDraftFileIds.length > 0) {
        setStagedFileIds((prev) => [...new Set([...prev, ...allDraftFileIds])]);
        setInitializedFromDraft(true);
      }
    }
  }, [draftStagedFiles, initializedFromDraft]);

  useEffect(() => {
    setRemovedDraftFileIds(new Set());
  }, [draftId]);

  // Notify parent when additional documents comments / linked studies / parent request are restored from a draft
  useEffect(() => {
    if (
      onDraftDataLoaded &&
      (draftAdditionalDocumentsComments ||
        draftLinkedRequestIds ||
        draftParentRequestIds)
    ) {
      onDraftDataLoaded({
        additionalDocumentsComments: draftAdditionalDocumentsComments,
        linkedRequestIds: draftLinkedRequestIds,
        parentRequestIds: draftParentRequestIds,
      });
    }
  }, [
    draftAdditionalDocumentsComments,
    draftLinkedRequestIds,
    draftParentRequestIds,
    onDraftDataLoaded,
  ]);

  // Fetch approval status for the request (persists across all stages/send-backs)
  useEffect(() => {
    if (!requestId) return;

    const fetchApprovals = async () => {
      try {
        const data = await getApprovalsByRequest(requestId);
        setSubformApprovals(data.approvalMap);
      } catch (err) {
        console.error('Error fetching approvals:', err);
      }
    };

    fetchApprovals();
  }, [requestId, getApprovalsByRequest]);

  // Get approval status for for a specific form's subforms
  const getFormApprovalStatus = useCallback(
    (formId) => {
      try {
        // Return the approvals for the specific form
        return subformApprovals[formId] || {};
      } catch (error) {
        console.error('Error getting form approval status:', error);
        return {};
      }
    },
    [subformApprovals]
  );

  // Get comment status for subforms (flattened - ignores form ID for customer view)
  const getFormCommentStatus = useCallback(() => {
    try {
      // Merge static commentsStatus with dynamically added localComments
      const status = {};

      // Include static commentsStatus from prop
      if (commentsStatus) {
        Object.assign(status, commentsStatus);
      }

      // Also include dynamically added comments from localComments
      localComments.forEach((comment) => {
        if (comment.subForm_id) {
          status[comment.subForm_id] = true;
        }
      });

      if (Object.keys(status).length > 0) {
        return status;
      }

      // Fallback: If comments prop is provided, flatten it
      if (Object.keys(comments).length > 0) {
        Object.values(comments).forEach((formComments) => {
          (formComments || []).forEach((comment) => {
            if (comment.subForm_id) {
              status[comment.subForm_id] = true;
            }
          });
        });
        return status;
      }

      // Priority 3: Empty object (no comments available)
      return {};
    } catch (error) {
      console.error('Error getting form comment status:', error);
      return {};
    }
  }, [commentsStatus, comments, localComments]);

  // Create stable refs for all forms
  useMemo(() => {
    forms.forEach((form) => {
      if (!formRefsMap.current[form.id]) {
        formRefsMap.current[form.id] = createRef();
      }
    });
  }, [forms]);

  // Create comments object for MultiFormView
  // If localComments is available (customer view), distribute to all forms
  // FormCrafterListView.getCommentsForSubform() will filter by subForm_id
  const commentsForView = useMemo(() => {
    // If localComments available (customer view with allComments prop), assign to each form
    if (localComments.length > 0) {
      const distributed = {};
      forms.forEach((form) => {
        distributed[form.id] = localComments;
      });
      return distributed;
    }
    // Otherwise use the comments prop (admin view style)
    return comments;
  }, [localComments, comments, forms]);

  const { activeIndex: activeSubformIndex, navigate: navigateSubform } =
    useSubformNavigation(activeSubforms);

  // Handle validation change from MultiFormView
  const handleValidationChange = useCallback(
    (formId, subformId, validationInfo) => {
      try {
        setValidationErrors((prev) => ({
          ...prev,
          [formId]: {
            ...prev[formId],
            [subformId]: validationInfo,
          },
        }));
      } catch (error) {
        console.error('Error updating validation errors:', error);
      }
    },
    []
  );

  // Check if any form has validation errors
  const hasAnyValidationErrors = useMemo(() => {
    return Object.values(validationErrors).some((formErrors) =>
      Object.values(formErrors || {}).some(
        (subformError) => subformError?.hasError
      )
    );
  }, [validationErrors]);

  // Disable comments for new requests (when workflowVersionId is provided but requestId is not)
  const isNewRequest = !!workflowVersionId && !requestId;
  // Use explicit prop if provided, otherwise fall back to internal logic
  const shouldEnableComments =
    enableAddCommentProp !== undefined
      ? enableAddCommentProp
      : isEditable && !isNewRequest;
  const shouldEnableFieldEdit = isEditable && !!requestId;
  // Show required indicators only when form is editable (applicant view)
  const showRequiredIndicator = !disabled && isEditable;

  // Parent Request selection is only required when creating (no existing request yet)
  const linkedStudiesRequired = !!linkedStudiesSelectorSlot;
  const parentRequestRequired = !!parentRequestSelectorSlot && !requestId;

  /**
   * Handler for eager file upload (called by FileFieldInput)
   *
   * This implements the "eager upload" pattern where files are uploaded immediately
   * when selected, rather than waiting for form submission. Benefits:
   * - Better UX with progress feedback
   * - Files saved even if user closes browser (then removed later if not used after x days)
   * - Enables Save As Draft with file attachments
   *
   * Flow:
   * 1. FileFieldInput calls this when user selects a file
   * 2. File uploaded to backend via useFilesApi (centralized file module)
   * 3. Backend saves to files table with STAGED status
   * 4. File ID tracked for later draft save or form submission
   * 5. Formatted file data returned for display in FileFieldInput
   *
   * @param {File} file - The file object from user selection
   * @param {Object} options - Upload context
   * @param {number} options.formId - Form ID for file scoping
   * @param {number} options.subformId - Subform ID
   * @param {string} options.fieldId - Field ID
   * @returns {Object|null} Formatted file data or null on failure
   */
  const handleStagedFileUpload = useCallback(
    async (file, options) => {
      try {
        const result = await uploadFile(file, options);
        if (result) {
          markInteracted();
          const normalizedNewId = normalizeFileId(result.id);
          // Create properly formatted file data for display, validation and submission
          // Include field metadata for backend to store in attachments table
          const fileData = {
            id: result.id,
            name: result.name || result.original_name || file.name,
            size: Number(result.size ?? result.file_size ?? file.size ?? 0),
            type: result.type || result.mime_type || file.type,
            stagedId: result.id,
            isStagedFile: true,
            isNewFileModule: result.isNewFileModule ?? true, // New file module uploads
            // Field metadata for attachment context
            formId: options?.formId || null,
            subformId: options?.subformId || null,
            fieldId: options?.fieldId || null,
          };

          // Add to staged file IDs for submission
          if (normalizedNewId) {
            setStagedFileIds((prev) => [
              ...new Set([...prev, normalizedNewId]),
            ]);
          }

          // Track by field key for validation
          const fieldKey = getFieldKeyFromOptions(options);
          if (fieldKey) {
            // Update ref synchronously for immediate validation access
            uploadedStagedFilesByFieldRef.current = {
              ...uploadedStagedFilesByFieldRef.current,
              [fieldKey]: [
                ...(uploadedStagedFilesByFieldRef.current[fieldKey] || []),
                fileData,
              ],
            };
            // Trigger memo recalculation to pick up ref changes immediately
            setStagedFilesTrigger((prev) => prev + 1);
            // Also update state for re-renders
            setUploadedStagedFilesByField((prev) => ({
              ...prev,
              [fieldKey]: [...(prev[fieldKey] || []), fileData],
            }));
          }

          // Return formatted file data for FileFieldInput to display correctly
          return fileData;
        }
        return null;
      } catch (error) {
        console.error('Error uploading staged file:', error);
        toast.error(t('fileUpload.uploadFailed', 'Failed to upload file'), {
          position: 'top-center',
        });
        return null;
      }
    },
    [uploadFile, getFieldKeyFromOptions, t, markInteracted]
  );

  /**
   * Handler for deleting a staged file (called by FileFieldInput)
   *
   * Removes the file from:
   * 1. Backend staged_files table (via API)
   * 2. stagedFileIds array (for submission tracking)
   * 3. uploadedStagedFilesByField (for validation)
   * 4. Adds to removedDraftFileIds (to filter from allStagedFiles)
   *
   * @param {number|string} fileId - The staged file ID to delete
   * @returns {boolean} True if deletion was successful
   */
  const handleStagedFileDelete = useCallback(
    async (fileId) => {
      try {
        const normalizedId = normalizeFileId(fileId);

        // Optimistic removal: remove from ALL states FIRST to prevent re-render
        // attempting to fetch/preview a deleted file
        setStagedFileIds((prev) =>
          prev.filter((id) => normalizeFileId(id) !== normalizedId)
        );
        if (normalizedId) {
          setRemovedDraftFileIds((prev) => new Set([...prev, normalizedId]));
        }
        // Remove from field tracking (ref - synchronous)
        const updatedRef = { ...uploadedStagedFilesByFieldRef.current };
        Object.keys(updatedRef).forEach((key) => {
          updatedRef[key] = updatedRef[key].filter(
            (f) =>
              normalizeFileId(f.id) !== normalizedId &&
              normalizeFileId(f.stagedId) !== normalizedId
          );
          if (updatedRef[key].length === 0) {
            delete updatedRef[key];
          }
        });
        uploadedStagedFilesByFieldRef.current = updatedRef;
        // Trigger memo recalculation to pick up ref changes immediately
        setStagedFilesTrigger((prev) => prev + 1);
        // Also update state for re-renders
        setUploadedStagedFilesByField((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((key) => {
            updated[key] = updated[key].filter(
              (f) =>
                normalizeFileId(f.id) !== normalizedId &&
                normalizeFileId(f.stagedId) !== normalizedId
            );
            if (updated[key].length === 0) {
              delete updated[key];
            }
          });
          return updated;
        });

        // Then delete from server (fire-and-forget OK since state is already clean)
        const success = await deleteFile(fileId);
        if (!success) {
          console.warn(
            `[handleStagedFileDelete] Server deletion failed for file ${fileId}, but state already cleaned`
          );
        }
        return success;
      } catch (error) {
        console.error('Error deleting staged file:', error);
        toast.error(t('fileUpload.deleteFailed', 'Failed to delete file'), {
          position: 'top-center',
        });
        return false;
      }
    },
    [deleteFile, t]
  );

  /**
   * Handler for confirmed deletion of server files (bulk).
   * Deletion is immediate in both draft and non-draft modes.
   * - If editing a draft: call updateDraftDeletedFiles (deletes from DraftAttachment + EndUserFilledFormAttachment + fileService)
   * - If not in draft: call deleteAttachedFiles (deletes from EndUserFilledFormAttachment + fileService)
   *
   * @param {Array<number|string>} fileIds - Array of file IDs to delete
   */
  const handleServerFileDeleteConfirmed = useCallback(
    async (fileIds) => {
      try {
        // Normalize: accept single ID or array
        const ids = Array.isArray(fileIds) ? fileIds : [fileIds];
        if (ids.length === 0) return;

        if (draftId) {
          // Draft mode: use dedicated draft deletion endpoint (already accepts array)
          const updated = await updateDraftDeletedFiles(draftId, ids);
          if (!updated) {
            toast.error(
              t(
                'drafts.failedToUpdateDeletedFiles',
                'Failed to update draft deletions'
              ),
              {
                position: 'top-center',
              }
            );
          }
        } else {
          // Non-draft mode: delete from attachments + file service (already accepts array)
          const success = await deleteAttachedFiles(ids);
          if (!success) {
            toast.error(t('fileUpload.deleteFailed', 'Failed to delete file'), {
              position: 'top-center',
            });
          }
        }
      } catch (error) {
        console.error('Error deleting server files:', error);
        toast.error(t('fileUpload.deleteFailed', 'Failed to delete file'), {
          position: 'top-center',
        });
      }
    },
    [draftId, updateDraftDeletedFiles, deleteAttachedFiles, t]
  );

  // Combine draft staged files with newly uploaded staged files for validation
  // Note: This uses both state (for re-renders) and ref (for immediate access)
  const allStagedFiles = useMemo(() => {
    const combined = {};

    // Add draft staged files (filter out deleted ones)
    Object.entries(draftStagedFiles || {}).forEach(([key, files]) => {
      const remaining = (files || []).filter(
        (file) =>
          !removedDraftFileIds.has(normalizeFileId(file.id || file.stagedId))
      );
      if (remaining.length > 0) {
        combined[key] = remaining;
      }
    });

    // Merge newly uploaded files
    Object.entries(uploadedStagedFilesByField).forEach(([key, files]) => {
      if (combined[key]) {
        // Append new files to existing
        const existingIds = new Set(
          combined[key].map((f) => normalizeFileId(f.id || f.stagedId))
        );
        const newFiles = files.filter(
          (f) => !existingIds.has(normalizeFileId(f.id || f.stagedId))
        );
        combined[key] = [...combined[key], ...newFiles];
      } else {
        combined[key] = files;
      }
    });

    // Also merge from ref (in case state hasn't updated yet)
    Object.entries(uploadedStagedFilesByFieldRef.current).forEach(
      ([key, files]) => {
        if (combined[key]) {
          const existingIds = new Set(
            combined[key].map((f) => normalizeFileId(f.id || f.stagedId))
          );
          const newFiles = files.filter(
            (f) => !existingIds.has(normalizeFileId(f.id || f.stagedId))
          );
          combined[key] = [...combined[key], ...newFiles];
        } else {
          combined[key] = files;
        }
      }
    );

    return combined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    draftStagedFiles,
    uploadedStagedFilesByField,
    removedDraftFileIds,
    stagedFilesTrigger,
  ]);

  const draftAttachedFileIds = useMemo(() => {
    const ids = new Set();
    Object.values(draftStagedFiles || {}).forEach((files) => {
      (files || []).forEach((file) => {
        const normalized = normalizeFileId(file.id || file.stagedId);
        if (normalized) {
          ids.add(normalized);
        }
      });
    });
    return ids;
  }, [draftStagedFiles]);

  // NOTE: mergedDraftDeletedFileIds simplified - file deletions are now immediate,
  // so we only track removedDraftFileIds (staged files removed from UI during this session).
  // draftDeletedFileIds from the backend is no longer needed since deletions happen immediately.
  const mergedDraftDeletedFileIds = useMemo(() => {
    return Array.from(removedDraftFileIds);
  }, [removedDraftFileIds]);

  // Handler for saving as draft
  const handleSaveAsDraft = useCallback(
    async (navigateAfterSave = true) => {
      // Get the stage ID from the forms if not provided via props
      // For new requests, the stage ID comes from the loaded workflow version stages
      const effectiveStageId =
        stageId || (forms.length > 0 ? forms[0].stageId : null);

      if (!effectiveStageId) {
        toast.error(t('multiFormSubmission.cannotSaveDraftNoStage'), {
          position: 'top-center',
        });
        return;
      }

      setSavingDraft(true);

      try {
        // Collect form data from all forms (without validation for drafts)
        const allFormData = {};

        for (const form of forms) {
          const formRef = formRefsMap.current[form.id];
          if (formRef?.current?.validateAndGetData) {
            const result = formRef.current.validateAndGetData();
            if (result.data && result.formId) {
              allFormData[result.formId] = result.data;
            }
            // NOTE: deletedFileIds no longer collected here - deletions are immediate
          }
        }

        // Build file_data with metadata from uploadedStagedFilesByFieldRef
        // This allows the backend to properly attach files with field context
        const fileDataArray = Object.entries(
          uploadedStagedFilesByFieldRef.current || {}
        ).flatMap(([fieldKey, files]) =>
          (files || [])
            .filter(
              (file) =>
                normalizeFileId(file.id || file.stagedId) &&
                file.isNewFileModule
            )
            .map((file) => ({
              id: normalizeFileId(file.id || file.stagedId),
              formId: file.formId || null,
              subformId: file.subformId || null,
              fieldId: file.fieldId || null,
              fieldKey,
            }))
        );

        // Create/update the draft with file_data
        // NOTE: deleted_file_ids no longer sent - deletions are immediate via dedicated endpoints
        const draft = await createOrUpdateDraft({
          workflow_version_id: workflowVersionId,
          request_id: requestId || null,
          draft_id: draftId || null,
          stage_id: effectiveStageId,
          form_data: allFormData,
          study_name: customRequestName || null,
          file_data: fileDataArray,
          additional_documents_comments: additionalDocumentsComments || null,
          linked_request_ids: linkedRequestIds || null,
          parent_request_ids: parentRequestIds || null,
        });

        if (draft && navigateAfterSave) {
          allowNavigation();
          navigate('/enduser/requests/my-requests', {
            state: { activeTab: 4 },
          });
        }
      } catch (error) {
        console.error('Error saving draft:', error);
        toast.error(t('multiFormSubmission.failedToSaveDraft'), {
          position: 'top-center',
        });
      } finally {
        setSavingDraft(false);
      }
    },
    [
      stageId,
      forms,
      workflowVersionId,
      requestId,
      draftId,
      customRequestName,
      stagedFileIds,
      additionalDocumentsComments,
      linkedRequestIds,
      parentRequestIds,
      createOrUpdateDraft,
      navigate,
      t,
      allowNavigation,
    ]
  );

  const collectSubmissionData = useCallback(() => {
    if (hasPendingFieldEdits) {
      toast.warning(t('multiFormSubmission.confirmOrCancelChanges'), {
        position: 'top-center',
      });
      return null;
    }

    if (linkedStudiesRequired && linkedRequestIds.length === 0) {
      onLinkedStudiesValidationError?.();
      toast.error(t('linkedStudiesRequired'), {
        position: 'top-center',
      });
      return null;
    }

    if (parentRequestRequired && parentRequestIds.length === 0) {
      toast.error(
        t(
          'parentRequest.required',
          'Please select an Approved Study Notification before submitting.'
        ),
        { position: 'top-center' }
      );
      return null;
    }

    const allFormData = {};
    let allValid = true;

    for (const form of forms) {
      const formRef = formRefsMap.current[form.id];
      if (formRef?.current?.validateAndGetData) {
        const result = formRef.current.validateAndGetData();
        if (!result.isValid) {
          allValid = false;
          const errorMessage = formatValidationError(result.errors);
          toast.error(
            errorMessage || t('multiFormSubmission.validationFailed'),
            {
              position: 'top-center',
            }
          );
          break;
        }
        if (result.data && result.formId) {
          allFormData[result.formId] = result.data;
        }
      }
    }

    if (!allValid) return null;

    const legacyStagedFileIds = new Set();
    const newFileModuleFiles = new Map();

    Object.entries(uploadedStagedFilesByFieldRef.current || {}).forEach(
      ([fieldKey, files]) => {
        (files || []).forEach((file) => {
          const id = normalizeFileId(file.id || file.stagedId);
          if (id) {
            if (file.isNewFileModule) {
              newFileModuleFiles.set(id, {
                id,
                formId: file.formId,
                subformId: file.subformId,
                fieldId: file.fieldId,
                fieldKey,
              });
            } else {
              legacyStagedFileIds.add(id);
            }
          }
        });
      }
    );

    stagedFileIds.forEach((id) => {
      const normalizedId = normalizeFileId(id);
      if (
        normalizedId &&
        (!draftId || !draftAttachedFileIds.has(normalizedId)) &&
        !newFileModuleFiles.has(normalizedId)
      ) {
        newFileModuleFiles.set(normalizedId, { id: normalizedId });
      }
    });

    return {
      allFormData,
      legacyStagedFileIds: Array.from(legacyStagedFileIds),
      newFileModuleFiles: Array.from(newFileModuleFiles.values()),
      stageId: forms.find((form) => !form.readOnly)?.stageId,
    };
  }, [
    draftAttachedFileIds,
    draftId,
    forms,
    hasPendingFieldEdits,
    linkedRequestIds,
    linkedStudiesRequired,
    parentRequestIds,
    parentRequestRequired,
    onLinkedStudiesValidationError,
    stagedFileIds,
    t,
  ]);

  const handleSubmitAllForms = useCallback(async () => {
    try {
      const submissionData = collectSubmissionData();
      if (!submissionData) return;
      const success = await handleSaveAllForms(
        submissionData.allFormData,
        [],
        additionalDocumentsComments,
        linkedRequestIds,
        submissionData.legacyStagedFileIds,
        submissionData.newFileModuleFiles,
        draftId,
        { parentRequestIds }
      );

      if (success) {
        navigateBack();
      }
    } catch (error) {
      console.error('Error submitting forms:', error);
      toast.error(
        t('multiFormSubmission.submissionFailed', 'Failed to submit forms'),
        {
          position: 'top-center',
        }
      );
    }
  }, [
    additionalDocumentsComments,
    collectSubmissionData,
    draftId,
    handleSaveAllForms,
    linkedRequestIds,
    parentRequestIds,
    navigateBack,
    t,
  ]);

  const handleClosePaymentModal = useCallback(() => {
    setShowPaymentModal(false);
  }, []);

  /**
   * Validate forms for payment without submitting
   * Returns validation result with form data if valid, null if invalid
   */
  const handleValidateForPayment = useCallback(async () => {
    try {
      const submissionData = collectSubmissionData();
      if (!submissionData) {
        return { success: false };
      }
      return {
        success: true,
        stageId: submissionData.stageId,
        allFormData: submissionData.allFormData,
        legacyStagedFileIds: submissionData.legacyStagedFileIds,
        newFileModuleFiles: submissionData.newFileModuleFiles,
      };
    } catch (error) {
      console.error('Validation error:', error);
      return { success: false };
    }
  }, [collectSubmissionData]);

  /**
   * Save draft for payment
   * Creates/updates draft which will be submitted after payment succeeds
   */
  const handleSaveDraftForPayment = useCallback(async () => {
    try {
      const submissionData = collectSubmissionData();
      if (!submissionData) {
        return {
          success: false,
          error: t('multiFormSubmission.validationFailed'),
        };
      }

      const effectiveStageId = stageId || submissionData.stageId;
      if (!effectiveStageId) {
        return {
          success: false,
          error: t('multiFormSubmission.cannotSaveDraftNoStage'),
        };
      }

      // Build file_data array for draft
      const fileDataArray = submissionData.newFileModuleFiles.map((file) => ({
        id: file.id,
        formId: file.formId || null,
        subformId: file.subformId || null,
        fieldId: file.fieldId || null,
        fieldKey: file.fieldKey || null,
      }));

      // Create/update draft - overrides any existing draft for this stage
      const draft = await createOrUpdateDraft({
        workflow_version_id: workflowVersionId,
        request_id: requestId || null,
        draft_id: draftId || null,
        stage_id: effectiveStageId,
        form_data: submissionData.allFormData,
        study_name: customRequestName || null,
        file_data: fileDataArray,
        additional_documents_comments: additionalDocumentsComments || null,
        linked_request_ids: linkedRequestIds || null,
        parent_request_ids: parentRequestIds || null,
      });

      if (!draft?.id) {
        return { success: false, error: t('payment.draftSaveFailed') };
      }

      // Allow navigation to payment gateway without unsaved changes warning
      allowNavigation();

      return {
        success: true,
        draftId: draft.id,
        stageId: effectiveStageId,
      };
    } catch (error) {
      console.error('Error saving draft for payment:', error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.message ||
          t('payment.draftSaveFailed'),
      };
    }
  }, [
    collectSubmissionData,
    createOrUpdateDraft,
    stageId,
    workflowVersionId,
    draftId,
    customRequestName,
    additionalDocumentsComments,
    linkedRequestIds,
    parentRequestIds,
    allowNavigation,
    t,
  ]);

  // Only show the spinner during initial data loading (fetching stages/forms).
  // During form submission (`submitting` is true), keep the forms mounted so
  // user input is preserved — if the backend rejects the request the user can
  // fix the errors and resubmit without losing their data.
  if (loading && !submitting) {
    return (
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={12}>
          <div className={styles.loadingContainer}>
            <CircularProgress />
          </div>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        {t('multiFormSubmission.errorLoadingForms')}: {error}
      </div>
    );
  }

  if (forms.length === 0) {
    return (
      <div className={styles.emptyContainer}>
        {t('multiFormSubmission.noSubmissionFormsFound')}
      </div>
    );
  }

  if (showHcpProfileFormSections) {
    return (
      <div
        className={styles.container}
        onChangeCapture={markInteracted}
        onInputCapture={markInteracted}
      >
        {topSlot}
        <MultiFormProvider
          workflowVersionId={workflowVersionId}
          requestId={requestId}
          requestProgressId={requestProgressId}
          studyName={customRequestName}
          isSendbackMode={isSendbackMode || !!requestId}
          draftId={draftId}
          licensingExperienceMode={licensingExperienceMode}
        >
          <HcpProfileFormSectionsPanel
            editable={!disabled}
            enableAddComment={shouldEnableComments}
            isSendbackMode={isSendbackMode || !!requestId}
          />
          <FormSectionsSubmissionActions disabled={disabled} />
        </MultiFormProvider>
        {additionalDocumentsSlot}
        {requestAuditTrailsSlot}
        {requestClarificationWavesSlot}
        {parentRequestSelectorSlot}
        {linkedStudiesSelectorSlot}
        {linkedStudiesViewSlot}
        {linkedSubmissionsViewSlot}
      </div>
    );
  }

  return (
    <div
      className={styles.container}
      onChangeCapture={markInteracted}
      onInputCapture={markInteracted}
    >
      <MultiFormView
        forms={forms}
        formData={formData}
        comments={commentsForView}
        onCommentsUpdate={handleCommentsUpdate}
        activeFormIndex={activeFormIndex}
        activeSubformIndex={activeSubformIndex}
        onFormTabChange={handleFormTabChange}
        onSubformNavigate={navigateSubform}
        disabled={disabled}
        useListView={useListView}
        formRefsMap={formRefsMap}
        requestAuditTrailsSlot={requestAuditTrailsSlot}
        additionalDocumentsSlot={additionalDocumentsSlot}
        requestClarificationWavesSlot={requestClarificationWavesSlot}
        linkedStudiesSelectorSlot={linkedStudiesSelectorSlot}
        linkedStudiesViewSlot={linkedStudiesViewSlot}
        linkedSubmissionsViewSlot={linkedSubmissionsViewSlot}
        parentRequestSelectorSlot={parentRequestSelectorSlot}
        hcpLicensesListSlot={hcpLicensesListSlot}
        showValidationIndicators={showValidationIndicators}
        onValidationChange={handleValidationChange}
        getFormApprovalStatus={getFormApprovalStatus}
        getFormCommentStatus={getFormCommentStatus}
        enableAddComment={shouldEnableComments}
        enableFieldEdit={shouldEnableFieldEdit}
        markPendingEdit={markPendingEdit}
        markResolvedEdit={markResolvedEdit}
        topSlot={topSlot}
        showRequiredIndicator={showRequiredIndicator}
        requestId={requestId}
        enableFieldWaves={useListView && !!requestId}
        onStagedFileUpload={handleStagedFileUpload}
        onStagedFileDelete={handleStagedFileDelete}
        eagerUploadEnabled={enableEagerUpload}
        draftStagedFiles={allStagedFiles}
        draftDeletedFileIds={mergedDraftDeletedFileIds}
        onServerFileDeleteConfirmed={handleServerFileDeleteConfirmed}
        requestProgress={requestProgress}
        requestDateEnded={requestDateEnded}
        currentUserEmail={currentUserEmail}
        onCommentUpdated={onCommentUpdated}
        onCommentDeleted={onCommentDeleted}
        onSubformCommentAdded={onSubformCommentAdded}
      />

      {!disabled &&
        activeFormIndex < forms.length &&
        !forms[activeFormIndex]?.readOnly && (
          <div className={styles.submitContainer}>
            {enableDrafts && (
              <button
                type="button"
                className={styles.draftButton}
                onClick={handleSaveAsDraft}
                disabled={savingDraft || submitting || draftLoading}
              >
                {savingDraft
                  ? t('multiFormSubmission.savingDraft')
                  : t('multiFormSubmission.saveAsDraft')}
              </button>
            )}
            <button
              type="button"
              className={`${styles.submitButton} ${
                hasAnyValidationErrors ? styles.submitButtonWithErrors : ''
              }`}
              onClick={
                requiresPayment
                  ? () => setShowPaymentModal(true)
                  : handleSubmitAllForms
              }
              disabled={submitting || savingDraft || !canSubmitWithPayment}
            >
              {submitting
                ? t('multiFormSubmission.submitting')
                : requiresPayment
                  ? t('multiFormSubmission.submitAndPay')
                  : t('multiFormSubmission.submitAllForms')}
            </button>
          </div>
        )}

      <PaymentConfirmationModal
        open={showPaymentModal}
        onClose={handleClosePaymentModal}
        onValidate={handleValidateForPayment}
        onSaveDraft={handleSaveDraftForPayment}
        amount={paymentAmount}
        currency={paymentCurrency}
        draftId={draftId}
        stageId={activeStage?.id}
        requestId={requestId}
      />
    </div>
  );
};

export default MultiFormSubmission;
